import { Box, Container, Tab, Tabs, Typography } from "@mui/material";
import Heading from "../common/Heading";
import { memo, useCallback, useEffect, useState } from "react";
import useAccount from "../../hooks/useAccount";
import ResourceExplorerMainPage from "./ResourceExplorerMainPage";
import ViewResources from "./ViewResources";
import { useListIndexesQuery, useListViewsQuery } from "../../generated";
import ResourceGroup from "./ResourceGroup";
import { useSelectedResourceTaggingResources } from "../../hooks/useSelectedResourceTaggingResources";
import ViewTaggedResources from "./ViewTaggedResources";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = memo(({ children, value, index, ...other }: TabPanelProps) => {
  return (
    <Container
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="span">{children}</Typography>
        </Box>
      )}
    </Container>
  );
});

/**
 * Used for tabs
 */
function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

/**
 * Main Page of ResourceExplorer
 */
export default function ResourceExplorer() {
	const [value, setValue] = useState(0);
	const { arn, defaultregion } = useAccount();
  const [region, setRegion] = useState(defaultregion);
  const [selectedView, setSelectedView] = useState("loading");
	const [disabled, setDisabled] = useState(false);
  const [step3Unlock, setStep3Unlock] = useState(false);
  const { selectedResources, resourcesSelected, regionsSelected } = useSelectedResourceTaggingResources();
  const { data: listViewsData, loading: listViewsLoading } = useListViewsQuery({
    variables: {
      arn, // value for 'arn'
      region, // value for 'region'
    },
    context: {
      apiName: "resource_explorer"
    },
  });

  //Set Selected View to be the default view first
  useEffect(() => {
    if (listViewsData?.listViews?.items) {
      const defaultView = listViewsData.listViews.items.find((i: any) => i.Type === "DEFAULT")?.ViewArn;
      if (defaultView) {
        setSelectedView(defaultView);
      }
    }
  }, [listViewsData]);

  const { data: listIndexesData, loading, refetch } = useListIndexesQuery({
    variables: {
      arn,// value for 'arn'
    },
    context: {
      apiName: "resource_explorer"
    },
  });

  useEffect(() => {
    if (listIndexesData?.listIndexes?.items?.length === 0 || listIndexesData?.listIndexes === null) {
      setDisabled(true);
    }
  }, [listIndexesData]);
	// Handles which tab to change to
	const handleChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	}, [setValue]);

	const tabPanels = [
    {
      index: 0,
      component: (
				<ResourceExplorerMainPage 
					arn={arn}
          region={region}
          setRegion={setRegion}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          listIndexesData={listIndexesData}
          listViews={listViewsData?.listViews}
          loading={loading}
          listViewsLoading={listViewsLoading}
          refetch={refetch}
          disabled={disabled}
          changeTab={(
						index: number,
					) => {
						setValue(index);
					}}
				/>
      ),
    },
    {
      index: 1,
      component: (
				<ViewResources
					arn={arn}
          region={defaultregion}
          selectedView={selectedView}
          setStep3Unlock={setStep3Unlock}
          selectedResources={selectedResources}
          changeTab={(
						index: number,
					) => {
						setValue(index);
					}}
				/>
      ),
    },
    {
      index: 2,
      component: (
				<ResourceGroup
					arn={arn}
          resourcesSelected={resourcesSelected}
          regionsSelected={regionsSelected}
          changeTab={(
						index: number,
					) => {
            setStep3Unlock(false)
						setValue(index);
					}}
				/>
      ),
    },
    {
      index: 3,
      component: (
        <ViewTaggedResources
					arn={arn}
          region={defaultregion}
          listViews={listViewsData?.listViews}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
				/>
      ),
    },
    // Add placeholders for other panels if needed
  ];

	return (
		<Container component="main" maxWidth="lg">
			<Heading heading={"AWS Resource Tagging"} />
			<Box sx={{ width: "100%" }}>
				<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
					<Tabs
						value={value}
						onChange={handleChange}
						textColor="secondary"
						indicatorColor="secondary"
						variant="fullWidth"
					>
						<Tab label="STEP 1: Create" {...a11yProps(0)} />
						<Tab label="STEP 2: Select Resources To Tag" disabled={disabled} {...a11yProps(1)} />
            <Tab label="STEP 3: Add Resource Tags" disabled={step3Unlock === false} {...a11yProps(2)} />
            <Tab label="View Tagged Resources" disabled={disabled} {...a11yProps(3)} />
					</Tabs>
				</Box>
				{tabPanels.map(({ index, component }) => (
          <TabPanel value={value} index={index} key={`MultiFramework-${index}`}>
            {component}
          </TabPanel>
        ))}
			</Box>
		</Container>
	);
}